// =============================================================================
// ImpersonationSearchBar — Recherche globale pour "se connecter en tant que"
//
// Extrait de Shell.tsx dans le cadre du chantier UX #4 (refactor Shell).
// Utilise par la topbar regulateur pour impersonifier un client ou partenaire.
// =============================================================================

import { useState, useEffect, useRef } from 'react'
import { LoadingSpinner } from '@cinqo/ui'
import { supabase } from '../../lib/supabase'

export interface SearchResult {
  id: string
  nom: string
  ville: string | null
  type: 'client' | 'partner'
}

export interface ImpersonationSearchBarProps {
  onSelect: (r: SearchResult) => void
}

export default function ImpersonationSearchBar({ onSelect }: ImpersonationSearchBarProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (query.length < 2) { setResults([]); return }
    setLoading(true)
    const timer = setTimeout(async () => {
      const [clientRes, partnerRes] = await Promise.all([
        supabase.from('clients').select('id, nom, ville').ilike('nom', `%${query}%`).order('nom').limit(5),
        supabase.from('partners').select('id, nom, ville').ilike('nom', `%${query}%`).order('nom').limit(5),
      ])
      const clients: SearchResult[] = (clientRes.data ?? []).map(c => ({ ...c, type: 'client' as const }))
      const partners: SearchResult[] = (partnerRes.data ?? []).map(p => ({ ...p, type: 'partner' as const }))
      setResults([...clients, ...partners])
      setOpen(true)
      setLoading(false)
    }, 250)
    return () => clearTimeout(timer)
  }, [query])

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <div ref={wrapperRef} className="relative">
      <div className="flex items-center gap-2 bg-surface-low rounded-lg px-3 py-1.5 border border-ink-ghost/30 focus-within:border-violet-500/40 focus-within:bg-violet-50 transition-colors">
        <svg className="w-3.5 h-3.5 text-ink-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); if (!e.target.value) setOpen(false) }}
          onFocus={() => { if (results.length > 0) setOpen(true) }}
          placeholder="Se connecter en tant que client ou partenaire..."
          className="bg-transparent text-caption text-ink placeholder:text-ink-muted focus:outline-none w-full min-w-0 font-medium"
        />
        {loading && <LoadingSpinner size="xs" />}
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 mt-1 w-[340px] bg-surface-card rounded-lg shadow-lg border border-ink-ghost/30 z-[100] overflow-hidden max-h-[360px] overflow-y-auto">
          {/* Clients section */}
          {results.some(r => r.type === 'client') && (
            <>
              <div className="px-4 py-1.5 bg-surface-low border-b border-ink-ghost/30">
                <span className="text-label font-semibold text-ink-muted uppercase tracking-wider">Clients</span>
              </div>
              {results.filter(r => r.type === 'client').map(r => (
                <button
                  key={r.id}
                  onClick={() => { onSelect(r); setQuery(''); setOpen(false) }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-violet-50 transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                    <span className="text-label font-bold text-violet-500">{r.nom.substring(0, 2).toUpperCase()}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-ink truncate">{r.nom}</p>
                    {r.ville && <p className="text-caption text-ink-muted truncate">{r.ville}</p>}
                  </div>
                  <span className="text-label font-medium px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-500 flex-shrink-0">Client</span>
                </button>
              ))}
            </>
          )}

          {/* Partners section */}
          {results.some(r => r.type === 'partner') && (
            <>
              <div className="px-4 py-1.5 bg-surface-low border-b border-ink-ghost/30">
                <span className="text-label font-semibold text-ink-muted uppercase tracking-wider">Partenaires</span>
              </div>
              {results.filter(r => r.type === 'partner').map(r => (
                <button
                  key={r.id}
                  onClick={() => { onSelect(r); setQuery(''); setOpen(false) }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-mint-50 transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-lg bg-mint-50 flex items-center justify-center flex-shrink-0">
                    <span className="text-label font-bold text-mint-700">{r.nom.substring(0, 2).toUpperCase()}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-ink truncate">{r.nom}</p>
                    {r.ville && <p className="text-caption text-ink-muted truncate">{r.ville}</p>}
                  </div>
                  <span className="text-label font-medium px-1.5 py-0.5 rounded-md bg-mint-50 text-mint-700 flex-shrink-0">Partenaire</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}
